import { Alert, CircularProgress, Stack } from "@mui/material";
import { Check, X } from "lucide-react";
import { useMemo, useState, type MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import {
  FavouriteCloseButton,
  FavouriteDescription,
  FavouritePanel,
  FavouriteSaveButton,
  FavouriteTitle,
  MfaMethodAction,
  MfaMethodButton,
  MfaMethodDetail,
  MfaMethodLabel,
  MfaMethodOption,
  MfaMethodSelectedIcon
} from "./ProfileMfaConfig.styles";
import {
  useMfaUserSettingsMutation,
  useMfaUserSettingsQuery,
  type MfaFactor
} from "../../queries/mfaPreferences";
import { useAuthStore } from "../../stores";
import { getMfaPreference } from "../../utils/mfa";

type MFA_FACTORS = Array<{
  action?: { label: string; onClick: MouseEventHandler<HTMLButtonElement> },
  detail?: string,
  label: string,
  value: MfaFactor
}>;

export function FavouriteMethodModal({ 
  onClose,
  openConfigureTotpModal,
  enabledFactors,
  onBusyChange,
  onSaved
}: { 
  onClose: () => void, 
  openConfigureTotpModal: () => void;
  onBusyChange: (busy: boolean) => void;
  onSaved: (method: MfaFactor) => void;
  enabledFactors: MfaFactor[]
}) {
  const { t } = useTranslation();
  const { isError, isPending, mutate, reset } = useMfaUserSettingsMutation();
  // Lo stato utente viene riletto a ogni apertura della modale (es. dopo la configurazione del TOTP).
  const userSettings = useMfaUserSettingsQuery({ refetchOnMount: "always" });
  const userEnabledFactors = useMemo(
    () => userSettings.data?.enabledFactors ?? [],
    [userSettings.data?.enabledFactors]
  );
  const initialMethod = userSettings.data?.preferredFactor ?? null;
  const [userSelectedMethod, setSelectedMethod] = useState<MfaFactor | null>(null);
  const selectedMethod = userSelectedMethod ?? initialMethod;
  const userData = useAuthStore((state) => state.userData);
  const MFA_FACTORS: MFA_FACTORS = useMemo(() => [
    {
      detail: userData?.email,
      label: t("mfa_select_email"),
      value: "EMAIL_OTP"
    },
    {
      action: { label: t("profile_mfa_manage_device"), onClick: openConfigureTotpModal },
      detail: userData?.email,
      label: t("mfa_select_totp"),
      value: "SOFTWARE_TOKEN_MFA"
    }
  ], [openConfigureTotpModal, t, userData?.email]);

  const hasChanged = selectedMethod !== null && selectedMethod !== initialMethod;
  // L'email non richiede configurazione: è attivabile se abilitata dall'azienda. Il TOTP va prima configurato.
  const isMethodSelectable = (method: MfaFactor) => method === "EMAIL_OTP"
    ? enabledFactors.includes(method)
    : userEnabledFactors.includes(method);
  const isSelectedMethodEnabled = selectedMethod !== null && isMethodSelectable(selectedMethod);
  const isUserSettingsReady = userSettings.isSuccess && !userSettings.isFetching;
  const isLoading = isPending || userSettings.isFetching;

  return (
    <FavouritePanel>
      <Stack alignItems="flex-start" direction="row" justifyContent="space-between">
        <Stack gap={ 2 }>
          <FavouriteTitle id="profile-mfa-favourite-title">
            { t("profile_mfa_manage_authentication") }
          </FavouriteTitle>
          <FavouriteDescription>
            { t("profile_mfa_favourite_description") }
          </FavouriteDescription>
        </Stack>

        <FavouriteCloseButton
          aria-label={ t("close") }
          disabled={ isPending }
          onClick={ onClose }
        >
          <X aria-hidden size={ 24 } />
        </FavouriteCloseButton>
      </Stack>

      <Stack gap={ 2 }>
        { MFA_FACTORS.filter((method) => enabledFactors.includes(method.value)).map((method) => {
          const isSelected = selectedMethod === method.value;

          return (
            <MfaMethodOption $isSelected={ isSelected } key={ method.value }>
              <MfaMethodButton
                aria-pressed={ isSelected }
                disabled={ isPending }
                onClick={ () => {
                  reset();
                  setSelectedMethod(method.value);
                } }
                type="button"
              >
                <Stack alignItems="center" direction="row" gap={ 2 } justifyContent="space-between">
                  <Stack gap={ 0.75 }>
                    <MfaMethodLabel $isSelected={ isSelected }>
                      { method.label }
                    </MfaMethodLabel>
                    <MfaMethodDetail>
                      { method.detail }
                    </MfaMethodDetail>
                  </Stack>

                  { isSelected ? (
                    <MfaMethodSelectedIcon>
                      <Check />
                    </MfaMethodSelectedIcon>
                  ) : null }
                </Stack>
              </MfaMethodButton>
              { method.action && <MfaMethodAction
                disabled={ isPending }
                onClick={ method.action.onClick }
                type="button"
              >
                { method.action.label }
              </MfaMethodAction> }
            </MfaMethodOption>
          );
        }) }
      </Stack>

      { isUserSettingsReady && selectedMethod !== null && !isSelectedMethodEnabled && (
        <Alert severity="info">{ t("profile_mfa_method_not_configured") }</Alert>
      ) }
      { userSettings.isError && <Alert severity="error">{ t("generic_error") }</Alert> }
      { isError && <Alert severity="error">{ t("profile_mfa_error_preference_save") }</Alert> }

      <FavouriteSaveButton
        disabled={ !hasChanged || !isSelectedMethodEnabled || !isUserSettingsReady || isPending }
        onClick={ () => {
          if (
            isPending
            || !isUserSettingsReady
            || !selectedMethod
            || !enabledFactors.includes(selectedMethod)
            || !isMethodSelectable(selectedMethod)
          ) return;
          onBusyChange(true);
          mutate({
            email_enabled: selectedMethod === "EMAIL_OTP" || userEnabledFactors.includes("EMAIL_OTP"),
            preferred_mfa: getMfaPreference(selectedMethod),
            totp_enabled: userEnabledFactors.includes("SOFTWARE_TOKEN_MFA")
          }, {
            onSettled: () => onBusyChange(false),
            onSuccess: () => onSaved(selectedMethod)
          });
        } }
        startIcon={ isLoading ? <CircularProgress color="inherit" size={ 16 } /> : undefined }
        type="button"
        variant="contained"
      >
        { t("save") }
      </FavouriteSaveButton>
    </FavouritePanel>
  );
}
